import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { Subject, Subscription } from "rxjs";
import { BfxBaseBlotter, provideBlotter } from "../bfx-base-blotter/bfx-base-blotter";
import { ApplicationRef, Component, ElementRef, Injector, NgZone } from "@angular/core";
import {
  BaseTableDefinitionFactory,
  PairVwap,
  ProfilePromiseActionService,
  SystemConfigPromiseService,
} from "shared/cubiq-services";
import { BfxVwapService } from "./bfx-vwap.service";
import { BfxVwapInfo, VWAPChangeNotification } from "./bfx-vwap-info";
import { BfxBaseBlotterService } from "app/bfx-base-blotter/bfx-base-blotter.service";
import { BfxBaseBlotterContextMenuService } from "app/bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { GoldenLayoutService } from "app/golden-layout/golden-layout-service";
import { TranslateService } from "@ngx-translate/core";
import { BfxBlotterFilterService } from "app/bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxBaseBlotterFilteringService } from "app/bfx-base-blotter/bfx-base-blotter-filtering.service";
import { BfxBaseBlotterColumnSelectorService } from "app/bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxSaveProfileNotificationService } from "app/bfx-common/services/bfx-save-profile-notification.service";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";
import { Vwap } from "shared/cubiq-services/mediator/IVwapService";
import { RowNode } from "ag-grid-community";

@Component({
  selector: "bfx-base-vwap-blotter",
  template: "",
  standalone: false,
})
export abstract class BfxBaseVwapBlotter extends BfxBaseBlotter {
  public windowUndocked: boolean = false;
  public vwapFooterText: string = "";

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    service: BfxBaseBlotterService,
    _contextMenuService: BfxBaseBlotterContextMenuService<any>,
    zone: NgZone,
    factory: BaseTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    filteringService: BfxBaseBlotterFilteringService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    userProfileService: BfxUserProfileService,
    protected vwapInfo: BfxVwapInfo,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuService,
      zone,
      factory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
    );
  }

  docked(event: any): void {
    this.publishVWAP("", this.getVwapSource());
    this.windowUndocked = false;
  }

  undocked(event: any): void {
    this.publishVWAP("", this.getVwapSource());
    this.windowUndocked = true;
  }

  abstract getVWapService(): BfxVwapService;

  getVwapSource(): string {
    return this.getBlotterId();
  }

  getNdfMode(): boolean {
    return false; // TODO
  }

  getVWapUnits() {
    return "orders";
  }

  public onSelectionChanged(selected?: RowNode[]): void {
    super.onSelectionChanged(selected);
    this.generateAndPublishVWAPString();
  }

  generateAndPublishVWAPString() {
    this.getVWAP().then((vwap) => {
      let foundPairs: Array<PairVwap> = vwap.pairVWaps;

      if (foundPairs.length < 1 || foundPairs.length > 1) {
        // no vwap with multiple pairs
        // foundPairs = [vwap.netVwap];
        this.publishVWAP("", this.getVwapSource());
      } else {
        let vwapString: string = this.generateVwapString(foundPairs[0], this.getVwapSource());
        this.publishVWAP(vwapString, this.getVwapSource());
      }
    });
  }

  /**
   * Calculate VWAP from the state of the blotter - normally based on selected row Ids
   * @returns
   */
  protected getVWAP(): Promise<Vwap> {
    let selectedIds: Array<string> = this.getOrdersIdsOfSelectedRows();
    return this.getVWAPsForSelection(selectedIds, this.getNdfMode());
  }

  getOrdersIdsOfSelectedRows(): Array<string> {
    let ret: Array<string> = [];

    let selectedRows = this.getSelectedRows();
    selectedRows.forEach((selectedRow) => {
      if (CubiqTS.Long.isLong(selectedRow.orderId)) {
        ret.push(selectedRow.orderId.toString());
      }
    });

    return ret;
  }

  getVWAPsForSelection(selectedIds: string[], ndfMode: boolean): Promise<Vwap> {
    return this.getVWapService().getPairsVwap(selectedIds, ndfMode);
  }

  generateVwapString(pair: PairVwap, source: string): string {
    return `${source}:&ensp;${pair.pairName}&ensp;<span class='base__vwap__intent'>${
      pair.intent
    }</span> <span class='base__vwap__amount'>${pair.amount}</span> at ${this.generateVwapRate(
      pair.rate,
      pair.pipIndex,
    )}`;
  }

  private generateVwapRate(rate: string, pipIndex: number): string {
    if (rate.includes(".")) {
      const i: number = pipIndex + rate.indexOf(".") - 1;
      const len = 2;

      return `<span class='base__vwap__rate'>${rate.slice(
        0,
        i,
      )}<span class='base__vwap__pips'>${rate.slice(i, i + len)}</span>${rate.slice(
        i + len,
      )}</span>`;
    }

    return `<span class='base__vwap__pips'>${rate}</span>`;
  }

  publishVWAP(vwapString: string, source: string) {
    if (this.windowUndocked) {
      this.setLocalVwap(vwapString, source);
    } else {
      this.pushVwapNotification(vwapString, source);
    }
  }

  setLocalVwap(vwapString: string, source: string) {
    this.vwapFooterText = vwapString;
  }

  pushVwapNotification(vwapString: string, source: string) {
    let notif: VWAPChangeNotification = new VWAPChangeNotification(vwapString, source);
    this.vwapInfo.push(notif);
  }
}
